import { PostLoginInterrupt } from './post-login-interrupt.model';

/**
 * INC-327 — queue arithmetic, kept free of Angular and of `NgbModal`.
 *
 * The ordering rule is the part of this Feature with a compliance consequence, so it
 * lives in pure functions that can be reasoned about and tested without a DOM, a
 * TestBed or a modal service.
 */

/**
 * Priority order, lowest number first, ties broken by arrival.
 *
 * Insertion order is the tiebreak on purpose: two interrupts of equal rank should show
 * up in the order the app discovered them, which is stable and explainable, rather than
 * in whatever order a sort happens to leave them.
 */
export function orderInterrupts(queue: readonly PostLoginInterrupt[]): PostLoginInterrupt[] {
  return queue
    .map((interrupt, arrival) => ({ interrupt, arrival }))
    .sort((a, b) => a.interrupt.priority - b.interrupt.priority || a.arrival - b.arrival)
    .map((entry) => entry.interrupt);
}

/**
 * Add, or replace in place if the key is already queued.
 *
 * Replacing rather than appending matters because the re-KYC nudge has two entry points
 * (ordinary login and login-on-behalf) that share a key. They can never both fire in one
 * session, but if they somehow did, the investor should see one nudge, not two.
 */
export function upsertInterrupt(
  queue: readonly PostLoginInterrupt[],
  interrupt: PostLoginInterrupt,
): PostLoginInterrupt[] {
  const existing = queue.findIndex((queued) => queued.key === interrupt.key);
  if (existing === -1) {
    return [...queue, interrupt];
  }
  const next = [...queue];
  next[existing] = interrupt;
  return next;
}

export function removeInterrupt(
  queue: readonly PostLoginInterrupt[],
  key: string,
): PostLoginInterrupt[] {
  return queue.filter((queued) => queued.key !== key);
}

/**
 * Stable identity of a queue's contents, for "is this already what's on screen?".
 *
 * Keys alone are not enough. `upsertInterrupt` replaces an item in place when its key is
 * already queued, so a re-enqueue under a live key with different copy — a re-read pending
 * vote naming a different company, say — changed the queue but not the signature, and the
 * frame went on rendering the snapshot it was opened with. Everything the cover and the
 * card actually draw goes in, so "replaces rather than stacks" reaches the screen.
 *
 * `component` is deliberately left out: a build that minifies class names cannot identify
 * one reliably, and no key in this design ever swaps its body.
 *
 * JSON rather than a joined string so a delimiter appearing inside translated copy cannot
 * forge a signature collision.
 */
export function queueSignature(queue: readonly PostLoginInterrupt[]): string {
  return JSON.stringify(
    orderInterrupts(queue).map((interrupt) => [
      interrupt.key,
      interrupt.kind,
      interrupt.priority,
      interrupt.label,
      interrupt.summary ?? '',
    ]),
  );
}
